import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Truck, Sparkles, ArrowRight, Skull, Ghost, Wand2, Bug, Star } from "lucide-react";
import { getProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CountdownTimer from "@/components/CountdownTimer";

const HERO_IMG =
  "https://images.unsplash.com/photo-1508361001413-7a9dca21d08a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const CATEGORIES = [
  { name: "Déguisements", icon: Wand2, img: "https://images.unsplash.com/photo-1699147528581-2d47d39fbc50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { name: "Décorations", icon: Ghost, img: "https://images.pexels.com/photos/14252774/pexels-photo-14252774.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Accessoires", icon: Skull, img: "https://images.unsplash.com/photo-1476370648495-3533f64427a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 8);
  const bestsellers = products.filter((p) => p.bestseller);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08060B] via-[#08060B]/85 to-[#08060B]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08060B] via-transparent to-[#08060B]/60" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-700/20 border border-red-600/40 mb-6">
              <Flame className="h-4 w-4 text-red-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-red-300">
                Offre de préparation Halloween 2026
              </span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black uppercase leading-[0.95] text-stone-50 mb-6">
              Réveillez la <span className="text-amber-500 text-glow">peur</span> en vous
            </h1>
            <p className="text-lg text-stone-300 leading-relaxed mb-8 max-w-xl">
              Déguisements terrifiants, décorations spectrales et accessoires maudits.
              Collections en édition limitée — <b className="text-amber-400">les stocks s'épuisent vite.</b>
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-10">
              <Link to="/boutique" className="btn-ember rounded-xl px-8 py-4 text-base inline-flex items-center gap-2" data-testid="hero-shop-button">
                Explorer la boutique <ArrowRight className="h-5 w-5" />
              </Link>
              <div className="inline-flex items-center gap-2 px-5 py-4 rounded-xl border border-amber-600/40 bg-black/30">
                <Sparkles className="h-4 w-4 text-amber-400" />
                <span className="text-sm text-stone-300">Code <b className="font-mono-tech text-amber-400">FRISSON20</b> · -20%</span>
              </div>
            </div>

            <div>
              <p className="eyebrow mb-3">La nuit d'Halloween approche…</p>
              <CountdownTimer />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORIES.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <Link
                to={`/boutique?cat=${encodeURIComponent(c.name)}`}
                className="group relative block rounded-2xl overflow-hidden h-56 gothic-card"
                data-testid={`category-${c.name.toLowerCase()}`}
              >
                <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08060B] via-[#08060B]/50 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-6">
                  <c.icon className="h-8 w-8 text-amber-500 mb-2" />
                  <h3 className="font-display text-2xl font-bold text-stone-50">{c.name}</h3>
                  <span className="text-sm text-amber-400 flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Découvrir <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* URGENCY STRIP */}
      <section className="border-y border-amber-900/30 bg-[#0b0810]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: Bug, t: "Stocks limités", s: "Éditions maudites en quantités réduites" },
            { icon: Truck, t: "Livraison offerte dès 40 €", s: "Expédition sous 48h" },
            { icon: Flame, t: "-20% avant Halloween", s: "Code FRISSON20 au paiement" },
          ].map((f) => (
            <div key={f.t} className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                <f.icon className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="font-bold text-stone-100">{f.t}</p>
                <p className="text-sm text-stone-500">{f.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BEST-SELLERS */}
      {bestsellers.length > 0 && (
        <section className="relative border-y border-amber-500/25 bg-gradient-to-b from-amber-950/25 via-amber-950/5 to-transparent" data-testid="bestsellers-section">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20">
            <div className="flex items-center gap-4 mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-extrabold text-xs uppercase tracking-widest shadow-lg">
                <Star className="h-4 w-4 fill-black" /> Les plus vendus
              </span>
              <p className="hidden sm:block text-sm text-stone-400">Les coups de cœur qui partent le plus vite cette saison</p>
              <div className="h-px flex-1 bg-amber-500/20" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {bestsellers.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
        <div className="flex items-end justify-between mb-10 gap-4">
          <div>
            <p className="eyebrow mb-2">Les incontournables</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-100">Sélection ensorcelée</h2>
          </div>
          <Link to="/boutique" className="hidden sm:inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 font-semibold text-sm">
            Tout voir <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
